import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2, Plus, Search, Upload, X } from 'lucide-react'
import { supabase } from '@/lib/supabase/client'
import { generateCTHReport } from '@/services/cthThreadPDFTemplate'
import { uploadDocument } from '@/services/document-upload.service'
import { ManualEquipmentCreationService } from '@/services/manualEquipmentCreation'
import { generateOrderNumber, getNextMethodReportSequence } from '@/utils/getNextReportSequence'
import { getNdtExitPath } from '@/utils/ndtNavigation'
import { completeJobAssignmentsForEquipment, NDT_REPORT_WORK_TYPES } from '@/services/jobs.service'
import { useNdtPersonnel } from '@/hooks/useNdtPersonnel'
import NdtFormPage, { NdtFormActions, ndtInputCls, ndtLabelCls, ndtSelectCls, ndtHintCls, ndtToggleCls, ndtUploadCls, ndtPrimaryBtnCls, ndtSecondaryBtnCls } from '@/components/ndt/NdtFormPage'
import {
  CTH_INSPECTED_SIDE_OPTIONS,
  CTH_TEST_SITES,
  OCTG_CATEGORY_CODE,
  THREAD_INSPECTION_KIND_OPTIONS,
  clearSideNo2,
  applyOctgSpecs,
  buildMpiInspectionTable,
  computeCTHTestSummary,
  createDefaultCTHFormData,
  dimensionalRowsForKind,
  enableSideVisualNo2,
  inspectedSidesFromThreadTypes,
  isBoxVisualRejected,
  isPinVisualRejected,
  mapCTHFormToPDFData,
  octgSpecEntries,
  sideHasNo2Data,
  visualRowsForKind,
  type CTHBodyVisual,
  type CTHBoxDimensional,
  type CTHBoxVisual,
  type CTHFormData,
  type CTHMeasurementPair,
  type CTHPinDimensional,
  type CTHPinVisual,
  type CTHVisualResult,
  type OctgSpecEntry,
} from '@/types/cthThreadTypes'

const sideHeaderCls = 'font-semibold text-[#1E3A5F] bg-[#E8EEF5]'
const cellInputCls =
  'w-full min-h-[44px] h-11 px-2 text-sm text-center border border-slate-300 bg-white rounded-md focus:outline-none focus:border-[#1E3A5F] text-slate-800'

interface ThreadObjectEquipment {
  id: string
  name: string
  serial_number: string | null
  part_number: string | null
  thread_type_pin: string | null
  thread_type_box: string | null
}

const getOneYearFromDate = (dateString: string): string => {
  const date = new Date(dateString)
  date.setFullYear(date.getFullYear() + 1)
  return date.toISOString().split('T')[0]
}

const specValueColumns = (entry: OctgSpecEntry) => {
  const cleared = {
    value_text: null as string | null,
    value_numeric: null as number | null,
    value_integer: null as number | null,
    value_date: null as string | null,
    value_boolean: null as boolean | null,
    value_reference_id: null as string | null,
  }
  if (entry.numeric && !entry.value.includes('/')) {
    const numeric = Number(entry.value.replace(',', '.'))
    if (Number.isFinite(numeric)) return { ...cleared, value_numeric: numeric }
  }
  return { ...cleared, value_text: entry.value }
}

const saveOctgSpecs = async (equipmentId: string, form: CTHFormData) => {
  const entries = octgSpecEntries(form)
  if (entries.length === 0) return
  const keys = entries.map((entry) => entry.specKey)
  const [{ data: existing }, { data: templates }, { data: { user } }] = await Promise.all([
    supabase.from('equipment_specifications_v2').select('id, spec_key').eq('equipment_id', equipmentId).in('spec_key', keys),
    supabase.from('canonical_specification_templates').select('id, spec_key, default_unit_id').in('spec_key', keys),
    supabase.auth.getUser(),
  ])
  const existingByKey = new Map((existing ?? []).map((row) => [row.spec_key, row.id]))
  const templateByKey = new Map((templates ?? []).map((row) => [row.spec_key, row]))

  await Promise.all(entries.map(async (entry) => {
    const existingId = existingByKey.get(entry.specKey)
    if (!entry.value) {
      if (!existingId) return
      const { error } = await supabase.from('equipment_specifications_v2').update({
        value_text: '',
        value_numeric: null,
        value_integer: null,
        value_date: null,
        value_boolean: null,
        value_reference_id: null,
        updated_by: user?.id,
      }).eq('id', existingId)
      if (error) throw error
      return
    }
    const values = specValueColumns(entry)
    if (existingId) {
      const { error } = await supabase.from('equipment_specifications_v2').update({ ...values, updated_by: user?.id }).eq('id', existingId)
      if (error) throw error
      return
    }
    const template = templateByKey.get(entry.specKey)
    if (!template) return
    const { error } = await supabase.from('equipment_specifications_v2').insert({
      equipment_id: equipmentId,
      template_id: template.id,
      spec_key: entry.specKey,
      unit_id: template.default_unit_id,
      created_by: user?.id,
      updated_by: user?.id,
      ...values,
    })
    if (error) throw error
  }))
}

const createOctgEquipment = async (form: CTHFormData): Promise<string> => {
  const { data: category, error: categoryError } = await supabase
    .from('categories_v2')
    .select('id')
    .eq('code', OCTG_CATEGORY_CODE[form.inspectionKind])
    .single()
  if (categoryError || !category) throw new Error('Fant ikke utstyrstype for OCTG')

  const created = await ManualEquipmentCreationService.createEquipment({
    name: form.objectDescription.trim(),
    serial_number: form.serialNo.trim(),
    part_number: form.partNo.trim() || undefined,
    description: form.assemblyDescription.trim() || undefined,
    company_id: form.company,
    category_id: category.id,
    status: 'Aktiv',
  })
  if (!created.success || !created.equipment?.id) {
    throw new Error(created.error || 'Kunne ikke opprette utstyr')
  }
  return created.equipment.id as string
}

const CTHTestForm = () => {
  const navigate = useNavigate()
  const { inspectors, admins, companies, currentUser, loading, isAdmin, isInspectorLocked } = useNdtPersonnel()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  const [formData, setFormData] = useState<CTHFormData>(() => createDefaultCTHFormData())
  const [pinNo2Enabled, setPinNo2Enabled] = useState(() => sideHasNo2Data(formData.pin, formData.visualPin))
  const [boxNo2Enabled, setBoxNo2Enabled] = useState(() => sideHasNo2Data(formData.box, formData.visualBox))

  const [equipmentSearch, setEquipmentSearch] = useState('')
  const [equipmentResults, setEquipmentResults] = useState<ThreadObjectEquipment[]>([])
  const [isSearchingEquipment, setIsSearchingEquipment] = useState(false)
  const [showEquipmentResults, setShowEquipmentResults] = useState(false)
  const [selectedEquipmentId, setSelectedEquipmentId] = useState<string | null>(null)
  const [operatorCertNo, setOperatorCertNo] = useState('')

  useEffect(() => {
    const name = formData.issuedBy.trim()
    if (!name) {
      setOperatorCertNo('')
      return
    }
    let cancelled = false
    const pattern = name.replace(/[%_]/g, '')
    void supabase
      .from('profiles')
      .select('full_name, mt_cert')
      .ilike('full_name', `%${pattern}%`)
      .then(({ data }) => {
        if (cancelled) return
        const match = (data ?? []).find((row) => (row.full_name ?? '').trim().toLowerCase() === name.toLowerCase())
        setOperatorCertNo(match?.mt_cert?.trim() || '')
      })
    return () => {
      cancelled = true
    }
  }, [formData.issuedBy])

  useEffect(() => {
    if (isInspectorLocked && currentUser?.full_name && !formData.issuedBy) {
      setFormData((prev) => ({ ...prev, issuedBy: currentUser.full_name }))
    }
  }, [isInspectorLocked, currentUser, formData.issuedBy])

  useEffect(() => {
    const companyId = formData.company?.trim()
    const term = equipmentSearch.trim()
    if (!companyId || term.length < 1) {
      setEquipmentResults([])
      setIsSearchingEquipment(false)
      return
    }

    let cancelled = false
    const handle = window.setTimeout(async () => {
      setIsSearchingEquipment(true)
      try {
        const { data, error } = await supabase
          .from('equipment_v2')
          .select('id, name, serial_number, part_number, categories_v2!inner(code)')
          .eq('company_id', companyId)
          .eq('categories_v2.code', OCTG_CATEGORY_CODE[formData.inspectionKind])
          .ilike('serial_number', `%${term}%`)
          .order('serial_number')
          .limit(20)

        if (cancelled) return
        if (error) {
          setEquipmentResults([])
          return
        }

        const rows = data ?? []
        const ids = rows.map((row) => row.id)
        const pinById = new Map<string, string | null>()
        const boxById = new Map<string, string | null>()

        if (ids.length > 0) {
          const { data: specs } = await supabase
            .from('equipment_specifications_v2')
            .select('equipment_id, spec_key, value_text')
            .in('spec_key', ['thread_type_pin', 'thread_type_box', 'octg_pin_connection', 'octg_box_connection'])
            .in('equipment_id', ids)

          for (const spec of specs ?? []) {
            if (spec.spec_key === 'thread_type_pin' || spec.spec_key === 'octg_pin_connection') pinById.set(spec.equipment_id, spec.value_text)
            if (spec.spec_key === 'thread_type_box' || spec.spec_key === 'octg_box_connection') boxById.set(spec.equipment_id, spec.value_text)
          }
        }

        setEquipmentResults(rows.map((row) => ({
          id: row.id,
          name: row.name,
          serial_number: row.serial_number,
          part_number: row.part_number,
          thread_type_pin: pinById.get(row.id) ?? null,
          thread_type_box: boxById.get(row.id) ?? null,
        })))
      } catch {
        if (!cancelled) setEquipmentResults([])
      } finally {
        if (!cancelled) setIsSearchingEquipment(false)
      }
    }, 300)

    return () => {
      cancelled = true
      window.clearTimeout(handle)
    }
  }, [equipmentSearch, formData.company, formData.inspectionKind])

  const summary = useMemo(
    () => computeCTHTestSummary(formData, { pinNo2: pinNo2Enabled, boxNo2: boxNo2Enabled }),
    [formData, pinNo2Enabled, boxNo2Enabled],
  )
  const mpiInspection = useMemo(
    () => buildMpiInspectionTable(formData, { pinNo2: pinNo2Enabled, boxNo2: boxNo2Enabled, operatorCertNo }),
    [formData, pinNo2Enabled, boxNo2Enabled, operatorCertNo],
  )
  const showPin = summary.pinInspected
  const showBox = summary.boxInspected
  const selectedCompanyName = useMemo(
    () => companies.find((c) => c.id === formData.company)?.name ?? '',
    [companies, formData.company],
  )

  const canSubmit = useMemo(() => {
    if (!formData.reportNumber.trim() || !formData.issuedBy.trim() || !formData.company.trim()) return false
    if (!formData.objectDescription.trim()) return false
    if (showPin && !summary.pinComplete) return false
    if (showBox && !summary.boxComplete) return false
    if (showPin && summary.pinRejected && !formData.pinRecommendation) return false
    if (showBox && summary.boxRejected && !formData.boxRecommendation) return false
    return true
  }, [formData, showPin, showBox, summary.pinComplete, summary.boxComplete, summary.bodyInspected, summary.bodyComplete, summary.pinRejected, summary.boxRejected])

  const submitBlocker = useMemo(() => {
    if (!formData.reportNumber.trim()) return 'Fyll ut ordrenummer.'
    if (!formData.company.trim()) return 'Velg kunde.'
    if (!formData.issuedBy.trim()) return 'Velg hvem som har utført kontrollen.'
    if (!formData.objectDescription.trim()) return 'Fyll ut object description.'
    if (showPin && !summary.pinComplete) return 'PIN: trykk hver grå OK-knapp til den blir grønn eller rød. Grå knapper er ikke registrert.'
    if (showBox && !summary.boxComplete) return 'BOX: trykk hver grå OK-knapp til den blir grønn eller rød. Grå knapper er ikke registrert.'
    if (showPin && summary.pinRejected && !formData.pinRecommendation) return 'Velg anbefaling for PIN.'
    if (showBox && summary.boxRejected && !formData.boxRecommendation) return 'Velg anbefaling for BOX.'
    return ''
  }, [formData, showPin, showBox, summary.pinComplete, summary.boxComplete, summary.bodyInspected, summary.bodyComplete, summary.pinRejected, summary.boxRejected])

  const handleCompanyChange = (companyId: string) => {
    const selected = companies.find((c) => c.id === companyId)
    setEquipmentSearch('')
    setEquipmentResults([])
    setSelectedEquipmentId(null)
    if (!selected) {
      setFormData((prev) => ({ ...prev, company: '', report_code: null }))
      return
    }
    const reportCode = selected.report_code || ''
    setFormData((prev) => ({
      ...prev,
      company: companyId,
      report_code: reportCode || null,
      reportNumber: reportCode ? generateOrderNumber(reportCode) : prev.reportNumber,
    }))
  }

  const handleSelectEquipment = async (equipment: ThreadObjectEquipment) => {
    setSelectedEquipmentId(equipment.id)
    setEquipmentSearch(equipment.serial_number || '')
    setShowEquipmentResults(false)

    const { data: specs } = await supabase
      .from('equipment_specifications_v2')
      .select('spec_key, value_text, value_numeric')
      .eq('equipment_id', equipment.id)
      .like('spec_key', 'octg_%')

    const stored = specs ?? []
    const fallback = {
      pinConnection: equipment.thread_type_pin,
      boxConnection: equipment.thread_type_box,
    }
    const flags = applyOctgSpecs(formData, stored, fallback)
    setPinNo2Enabled(flags.pinNo2)
    setBoxNo2Enabled(flags.boxNo2)
    setFormData((prev) => {
      const imported = applyOctgSpecs(prev, stored, fallback)
      return {
        ...imported.form,
        objectDescription: equipment.name || prev.objectDescription,
        serialNo: equipment.serial_number || '',
        partNo: equipment.part_number || '',
      }
    })
  }

  const handleDimensionalChange = (
    side: 'pin' | 'box',
    field: string,
    slot: keyof CTHMeasurementPair,
    value: string,
  ) => {
    setFormData((prev) => {
      if (side === 'pin') {
        const key = field as keyof CTHPinDimensional
        return { ...prev, pin: { ...prev.pin, [key]: { ...prev.pin[key], [slot]: value } } }
      }
      const key = field as keyof CTHBoxDimensional
      return { ...prev, box: { ...prev.box, [key]: { ...prev.box[key], [slot]: value } } }
    })
  }

  const reportMethod = formData.inspectionKind

  const toggleVisual = (
    side: 'visualPin' | 'visualBox',
    criterion: string,
    slot: keyof CTHMeasurementPair,
    current: CTHVisualResult,
  ) => {
    const next: CTHVisualResult = current === '' ? 'OK' : current === 'OK' ? 'REJ' : ''
    setFormData((prev) => {
      if (side === 'visualPin') {
        const key = criterion as keyof CTHPinVisual
        const nextVisual: CTHPinVisual = {
          ...prev.visualPin,
          [key]: { ...prev.visualPin[key], [slot]: next },
        }
        return {
          ...prev,
          visualPin: nextVisual,
          pinRecommendation: isPinVisualRejected(nextVisual, prev.inspectionKind, pinNo2Enabled) ? prev.pinRecommendation : '',
        }
      }
      const key = criterion as keyof CTHBoxVisual
      const nextVisual: CTHBoxVisual = {
        ...prev.visualBox,
        [key]: { ...prev.visualBox[key], [slot]: next },
      }
      return {
        ...prev,
        visualBox: nextVisual,
        boxRecommendation: isBoxVisualRejected(nextVisual, prev.inspectionKind, boxNo2Enabled) ? prev.boxRecommendation : '',
      }
    })
  }

  const toggleBodyVisual = (key: keyof CTHBodyVisual) => {
    setFormData((prev) => ({
      ...prev,
      visualBody: {
        ...prev.visualBody,
        [key]: prev.visualBody[key] === '' ? 'OK' : prev.visualBody[key] === 'OK' ? 'REJ' : '',
      },
    }))
  }

  const handlePreview = async () => {
    setIsGeneratingPreview(true)
    try {
      const { reportId } = await getNextMethodReportSequence(formData.inspectionKind, formData.reportNumber)
      const blob = await generateCTHReport(mapCTHFormToPDFData(formData, reportId, selectedCompanyName, { pinNo2: pinNo2Enabled, boxNo2: boxNo2Enabled, operatorCertNo }))
      setPreviewUrl(URL.createObjectURL(blob))
    } catch (error) {
      console.error(error)
      alert('Kunne ikke generere forhåndsvisning.')
    } finally {
      setIsGeneratingPreview(false)
    }
  }

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!canSubmit) {
      alert(submitBlocker || 'Vennligst fyll ut alle påkrevde felt.')
      return
    }
    setIsSubmitting(true)
    try {
      const equipmentId = selectedEquipmentId ?? await createOctgEquipment(formData)
      await saveOctgSpecs(equipmentId, formData)
      const { storageFolder, reportId } = await getNextMethodReportSequence(formData.inspectionKind, formData.reportNumber)
      const fileName = `${reportId}.pdf`
      const folderPath = `${storageFolder}/${fileName}`
      const pdfPayload = mapCTHFormToPDFData(formData, reportId, selectedCompanyName, { pinNo2: pinNo2Enabled, boxNo2: boxNo2Enabled, operatorCertNo })
      const pdfBlob = await generateCTHReport(pdfPayload)

      const { error: uploadError } = await supabase.storage
        .from('reports')
        .upload(folderPath, pdfBlob, { contentType: 'application/pdf', cacheControl: '3600' })
      if (uploadError) throw uploadError

      const { data: { user } } = await supabase.auth.getUser()
      const { attachedImage: _omit, ...stored } = formData
      const insertData: Record<string, unknown> = {
        order_number: reportId,
        unit_id: formData.serialNo || formData.objectDescription,
        test_date: formData.testDate,
        test_type: formData.inspectionKind,
        status: summary.rejected > 0 ? 'Not Accepted' : 'Accepted',
        issued_by: formData.issuedBy,
        verified_by: formData.verifiedBy?.trim() || null,
        file_path: folderPath,
        report_code: formData.report_code,
        created_by: user?.id,
        report_data: { ...stored, equipmentId },
      }
      if (formData.company?.trim()) insertData.company_id = formData.company

      const { error: metadataError } = await supabase.from('ndt_reports').insert(insertData)
      if (metadataError) throw metadataError

      const equipmentDocFile = new File([pdfBlob], fileName, { type: 'application/pdf' })
      const equipmentUpload = await uploadDocument({
        file: equipmentDocFile,
        customerName: selectedCompanyName || 'unknown',
        serialNumber: formData.serialNo || reportId,
        equipmentId,
        documentType: 'thread_report',
      })
      if (!equipmentUpload.success) {
        throw new Error(equipmentUpload.error || 'Kunne ikke lagre gjengerapport på utstyr')
      }

      const inspectionStatus = summary.rejected > 0 ? 'NC' : 'OK'
      const { error: dateUpdateError } = await supabase
        .from('equipment_v2')
        .update({
          last_inspection_date: formData.testDate,
          next_inspection_date: getOneYearFromDate(formData.testDate),
          inspection_status: inspectionStatus,
        })
        .eq('id', equipmentId)
      if (dateUpdateError) throw new Error('Rapport lagret, men inspeksjonsdatoer kunne ikke oppdateres')

      const { data: signed } = await supabase.storage.from('reports').createSignedUrl(folderPath, 60)
      if (signed?.signedUrl) window.open(signed.signedUrl, '_blank')

      alert(selectedEquipmentId
        ? 'Rapport generert, lagret på utstyr og inspeksjonsdatoer oppdatert.'
        : `Rapport generert og nytt ${formData.inspectionKind}-utstyr opprettet.`)
      if (selectedEquipmentId) void completeJobAssignmentsForEquipment(selectedEquipmentId, NDT_REPORT_WORK_TYPES)
      navigate(getNdtExitPath())
    } catch (error) {
      console.error(error)
      alert(error instanceof Error ? error.message : 'Feil ved generering av rapport. Prøv igjen.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onloadend = () => setFormData((prev) => ({ ...prev, attachedImage: reader.result as string }))
    reader.readAsDataURL(file)
  }

  return (
    <NdtFormPage
      highlight="Gjenge"
      title="inspeksjon"
      loading={loading}
      previewUrl={previewUrl}
      previewTitle={`${reportMethod}-rapport forhåndsvisning`}
      maxWidthClass="max-w-4xl"
      onClosePreview={() => {
        if (previewUrl) URL.revokeObjectURL(previewUrl)
        setPreviewUrl(null)
      }}
      onPublish={() => void handleSubmit()}
      isPublishing={isSubmitting}
      canPublish={canSubmit}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className={ndtLabelCls}>Type</label>
          <div className="flex gap-2">
            {THREAD_INSPECTION_KIND_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  setSelectedEquipmentId(null)
                  setEquipmentSearch('')
                  setEquipmentResults([])
                  setFormData((p) => ({ ...p, inspectionKind: option.value }))
                }}
                className={ndtToggleCls(formData.inspectionKind === option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={ndtLabelCls}>Testdato</label>
            <input type="date" required value={formData.testDate} onChange={(e) => setFormData((p) => ({ ...p, testDate: e.target.value }))} className={ndtInputCls} />
          </div>
          <div>
            <label className={ndtLabelCls}>Teststed</label>
            <select value={formData.testSite} onChange={(e) => setFormData((p) => ({ ...p, testSite: e.target.value }))} className={ndtSelectCls}>
              {CTH_TEST_SITES.map((site) => <option key={site} value={site}>{site}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={ndtLabelCls}>Kunde</label>
            <select value={formData.company || ''} onChange={(e) => handleCompanyChange(e.target.value)} className={ndtSelectCls}>
              <option value="">Velg kunde</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className={ndtLabelCls}>Vårt ordrenummer</label>
            <input type="text" required value={formData.reportNumber} onChange={(e) => setFormData((p) => ({ ...p, reportNumber: e.target.value }))} className={ndtInputCls} />
          </div>
        </div>

        <div>
          <label className={ndtLabelCls}>Søk utstyr (SN)</label>
          <p className={ndtHintCls}>
            {formData.company
              ? `Søk eksisterende ${formData.inspectionKind}-utstyr, eller la feltet stå tomt for å opprette nytt ved publisering`
              : 'Velg kunde først'}
          </p>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={equipmentSearch}
              disabled={!formData.company}
              onChange={(e) => {
                setSelectedEquipmentId(null)
                setEquipmentSearch(e.target.value)
                setShowEquipmentResults(true)
              }}
              onFocus={() => { if (equipmentResults.length > 0) setShowEquipmentResults(true) }}
              onBlur={() => window.setTimeout(() => setShowEquipmentResults(false), 150)}
              placeholder={formData.company ? 'Søk serienummer…' : 'Velg kunde først'}
              className={`${ndtInputCls} pl-9`}
            />
            {isSearchingEquipment && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />}
            {showEquipmentResults && formData.company && equipmentSearch.trim() && (
              <div className="absolute z-20 mt-1 w-full max-h-56 overflow-auto border border-slate-200 bg-white rounded-md shadow-lg">
                {equipmentResults.length === 0 && !isSearchingEquipment ? (
                  <div className="px-3 py-2 text-sm text-slate-400">Ingen {formData.inspectionKind}-utstyr funnet</div>
                ) : equipmentResults.map((eq) => (
                  <button key={eq.id} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => handleSelectEquipment(eq)} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-100 last:border-b-0">
                    <div className="font-medium text-slate-800">{eq.serial_number || 'Uten SN'}</div>
                    <div className="text-xs text-slate-400">{eq.name}{eq.part_number ? ` · PN ${eq.part_number}` : ''}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={ndtLabelCls}>Object Description</label>
            <input type="text" required value={formData.objectDescription} onChange={(e) => setFormData((p) => ({ ...p, objectDescription: e.target.value }))} className={ndtInputCls} />
          </div>
          <div>
            <label className={ndtLabelCls}>Assembly Description</label>
            <input type="text" value={formData.assemblyDescription} onChange={(e) => setFormData((p) => ({ ...p, assemblyDescription: e.target.value }))} className={ndtInputCls} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={ndtLabelCls}>Serial No</label>
            <input type="text" value={formData.serialNo} onChange={(e) => setFormData((p) => ({ ...p, serialNo: e.target.value }))} className={ndtInputCls} />
          </div>
          <div>
            <label className={ndtLabelCls}>Part No</label>
            <input type="text" value={formData.partNo} onChange={(e) => setFormData((p) => ({ ...p, partNo: e.target.value }))} className={ndtInputCls} />
          </div>
        </div>

        <div>
          <label className={ndtLabelCls}>Material</label>
          <input type="text" value={formData.material} onChange={(e) => setFormData((p) => ({ ...p, material: e.target.value }))} className={ndtInputCls} />
        </div>

        <div>
          <label className={ndtLabelCls}>Tilkobling</label>
          <div className="flex gap-2">
            {CTH_INSPECTED_SIDE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setFormData((p) => ({ ...p, inspectedSides: option.value }))}
                className={ndtToggleCls(formData.inspectedSides === option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <DimensionalTable
          pin={formData.pin}
          box={formData.box}
          inspectionKind={formData.inspectionKind}
          showPin={showPin}
          showBox={showBox}
          pinNo2Enabled={pinNo2Enabled}
          boxNo2Enabled={boxNo2Enabled}
          onChange={handleDimensionalChange}
          onAddNo2={(side) => {
            if (side === 'pin') {
              setPinNo2Enabled(true)
              setFormData((p) => ({ ...p, visualPin: enableSideVisualNo2(p.visualPin) }))
            } else {
              setBoxNo2Enabled(true)
              setFormData((p) => ({ ...p, visualBox: enableSideVisualNo2(p.visualBox) }))
            }
          }}
          onRemoveNo2={(side) => {
            if (side === 'pin') {
              setPinNo2Enabled(false)
              setFormData((p) => {
                const cleared = clearSideNo2(p.pin, p.visualPin)
                return { ...p, pin: cleared.dimensional, visualPin: cleared.visual }
              })
            } else {
              setBoxNo2Enabled(false)
              setFormData((p) => {
                const cleared = clearSideNo2(p.box, p.visualBox)
                return { ...p, box: cleared.dimensional, visualBox: cleared.visual }
              })
            }
          }}
        />

        {formData.inspectionKind === 'RSC' && (
          <div className="overflow-x-auto border border-slate-200 rounded-md bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className={`px-2 py-3 text-left ${sideHeaderCls}`}>BODY</th>
                  <th className={`px-2 py-3 text-left ${sideHeaderCls}`}>Tool Length</th>
                  <th className={`px-2 py-3 text-left ${sideHeaderCls}`}>OD</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="px-2 py-2 bg-slate-50 font-medium whitespace-nowrap">Verdi</td>
                  <td className="px-1 py-1">
                    <input
                      value={formData.bodyToolLength}
                      onChange={(e) => setFormData((p) => ({ ...p, bodyToolLength: e.target.value }))}
                      className={cellInputCls}
                    />
                  </td>
                  <td className="px-1 py-1">
                    <input
                      value={formData.bodyOd}
                      onChange={(e) => setFormData((p) => ({ ...p, bodyOd: e.target.value }))}
                      className={cellInputCls}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        <VisualTable
          visualPin={formData.visualPin}
          visualBox={formData.visualBox}
          visualBody={formData.visualBody}
          inspectionKind={formData.inspectionKind}
          showPin={showPin}
          showBox={showBox}
          onToggleBody={toggleBodyVisual}
          pinNo2Enabled={pinNo2Enabled}
          boxNo2Enabled={boxNo2Enabled}
          onToggle={toggleVisual}
          onAddNo2={(side) => {
            if (side === 'pin') {
              setPinNo2Enabled(true)
              setFormData((p) => ({ ...p, visualPin: enableSideVisualNo2(p.visualPin) }))
            } else {
              setBoxNo2Enabled(true)
              setFormData((p) => ({ ...p, visualBox: enableSideVisualNo2(p.visualBox) }))
            }
          }}
          onRemoveNo2={(side) => {
            if (side === 'pin') {
              setPinNo2Enabled(false)
              setFormData((p) => {
                const cleared = clearSideNo2(p.pin, p.visualPin)
                return { ...p, pin: cleared.dimensional, visualPin: cleared.visual }
              })
            } else {
              setBoxNo2Enabled(false)
              setFormData((p) => {
                const cleared = clearSideNo2(p.box, p.visualBox)
                return { ...p, box: cleared.dimensional, visualBox: cleared.visual }
              })
            }
          }}
        />

        {mpiInspection.visible && (
          <div className="border border-slate-200 rounded-md overflow-x-auto bg-white text-xs">
            <div className="px-2 py-1.5 bg-slate-50 font-semibold text-center border-b border-slate-200 whitespace-nowrap">
              MAGNETIC PARTICLE INSPECTION
            </div>
            {chunkMpiRows(mpiInspection.rows).map((group, index) => (
              <div key={index} className="grid grid-cols-3 min-w-[52rem] border-b border-slate-200 last:border-b-0">
                {group.map((field, fieldIndex) => (
                  <div key={`${field.label}-${fieldIndex}`} className="flex items-center gap-2 px-2 py-1 border-l border-slate-200 first:border-l-0 whitespace-nowrap">
                    <span className="font-medium text-slate-500">{field.label}</span>
                    <span className="text-slate-800">{field.value || '—'}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        <div className="border border-slate-200 rounded-md overflow-hidden bg-white text-sm">
          <div className={`${summaryGridCls(showPin, summary.bodyInspected, showBox)} border-b border-slate-200 bg-slate-50 font-semibold`}>
            <div className="px-3 py-3" />
            {showPin && <div className={`px-3 py-3 text-center ${sideHeaderCls}`}>PIN</div>}
            {summary.bodyInspected && <div className={`px-3 py-3 text-center ${sideHeaderCls}`}>BODY</div>}
            {showBox && <div className={`px-3 py-3 text-center ${sideHeaderCls}`}>BOX</div>}
          </div>
          <div className={`${summaryGridCls(showPin, summary.bodyInspected, showBox)} border-b border-slate-200`}>
            <div className="px-3 py-2.5 bg-slate-50 font-semibold">Accepted</div>
            {showPin && <SummaryStatus ok={summary.pinOk} rejected={summary.pinRejected} />}
            {summary.bodyInspected && <SummaryStatus ok={summary.bodyOk} rejected={summary.bodyRejected} />}
            {showBox && <SummaryStatus ok={summary.boxOk} rejected={summary.boxRejected} />}
          </div>
          {([
            { value: 'reface' as const, label: 'Reface recommended' },
            { value: 'recut' as const, label: 'Recut recommended' },
            { value: 'decommission' as const, label: 'Decomissioning recommended' },
          ]).map((row) => {
            const toggleSide = (side: 'pin' | 'box') => {
              const key = side === 'pin' ? 'pinRecommendation' : 'boxRecommendation'
              setFormData((p) => ({
                ...p,
                [key]: p[key] === row.value ? '' : row.value,
              }))
            }
            return (
              <div key={row.value} className={`${summaryGridCls(showPin, summary.bodyInspected, showBox)} border-b border-slate-200 last:border-b-0`}>
                <div className="px-3 py-2.5 bg-slate-50 font-semibold">{row.label}</div>
                {showPin && (
                  <label className={`px-3 py-3 inline-flex items-center justify-center ${summary.pinRejected ? '' : 'opacity-40'}`}>
                    <input
                      type="checkbox"
                      className="w-5 h-5 accent-[#1E3A5F]"
                      checked={summary.pinRejected && formData.pinRecommendation === row.value}
                      disabled={!summary.pinRejected}
                      onChange={() => toggleSide('pin')}
                    />
                  </label>
                )}
                {summary.bodyInspected && <div />}
                {showBox && (
                  <label className={`px-3 py-3 inline-flex items-center justify-center ${summary.boxRejected ? '' : 'opacity-40'}`}>
                    <input
                      type="checkbox"
                      className="w-5 h-5 accent-[#1E3A5F]"
                      checked={summary.boxRejected && formData.boxRecommendation === row.value}
                      disabled={!summary.boxRejected}
                      onChange={() => toggleSide('box')}
                    />
                  </label>
                )}
              </div>
            )
          })}
        </div>

        <div className="grid grid-cols-2 gap-3">
          {isAdmin && (
            <div>
              <label className={ndtLabelCls}>Utført av</label>
              <select required value={formData.issuedBy} onChange={(e) => setFormData((p) => ({ ...p, issuedBy: e.target.value }))} className={ndtSelectCls}>
                <option value="">Velg inspektør</option>
                {inspectors.map((i) => <option key={i.id} value={i.full_name}>{i.full_name}</option>)}
              </select>
            </div>
          )}

          <div>
            <label className={ndtLabelCls}>Ordreansvarlig</label>
            <select value={formData.verifiedBy || ''} onChange={(e) => setFormData((p) => ({ ...p, verifiedBy: e.target.value }))} className={ndtSelectCls}>
              <option value="">Ingen</option>
              {admins.map((a) => <option key={a.id} value={a.full_name}>{a.full_name}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className={ndtLabelCls}>Bilde (valgfritt)</label>
          {!formData.attachedImage ? (
            <label className={ndtUploadCls}>
              <Upload size={16} className="text-slate-400" />
              <span className="text-sm text-slate-600">Last opp bilde</span>
              <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
            </label>
          ) : (
            <div className="relative">
              <img src={formData.attachedImage} alt="Vedlegg" className="w-full h-64 object-cover rounded-md border border-slate-200" />
              <button type="button" onClick={() => setFormData((p) => ({ ...p, attachedImage: undefined }))} className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-full">
                <X size={20} />
              </button>
            </div>
          )}
        </div>

        {submitBlocker && (
          <p className="text-sm text-amber-700">{submitBlocker}</p>
        )}

        <NdtFormActions>
          <button type="button" onClick={() => void handlePreview()} disabled={isSubmitting || isGeneratingPreview} className={ndtSecondaryBtnCls}>
            {isGeneratingPreview ? 'Genererer…' : 'Forhåndsvis'}
          </button>
          <button type="submit" disabled={isSubmitting || !canSubmit} className={ndtPrimaryBtnCls}>
            {isSubmitting ? 'Genererer rapport...' : `Generer ${reportMethod}-rapport`}
          </button>
        </NdtFormActions>
      </form>
    </NdtFormPage>
  )
}

const chunkMpiRows = (rows: { label: string; value: string }[]) => {
  const pairs: { label: string; value: string }[][] = []
  for (let index = 0; index < rows.length; index += 3) {
    pairs.push(rows.slice(index, index + 3))
  }
  return pairs
}

const summaryGridCls = (showPin: boolean, showBody: boolean, showBox: boolean) => {
  const count = 1 + Number(showPin) + Number(showBody) + Number(showBox)
  if (count === 4) return 'grid grid-cols-[1.2fr_1fr_1fr_1fr]'
  if (count === 3) return 'grid grid-cols-[1.2fr_1fr_1fr]'
  return 'grid grid-cols-[1.2fr_1fr]'
}

const No2Header = ({
  enabled,
  side,
  onAdd,
  onRemove,
}: {
  enabled: boolean
  side: 'pin' | 'box'
  onAdd: (side: 'pin' | 'box') => void
  onRemove: (side: 'pin' | 'box') => void
}) => (
  <th className={`px-2 py-2.5 text-center border-b border-[#D5DEE9] ${sideHeaderCls}`}>
    {enabled ? (
      <span className="inline-flex items-center justify-center gap-1">
        No 2
        <button type="button" onClick={() => onRemove(side)} className="p-1 text-red-600"><X className="w-4 h-4" /></button>
      </span>
    ) : (
      <button type="button" onClick={() => onAdd(side)} className="inline-flex items-center justify-center w-11 h-11 border border-dashed border-slate-300 rounded-md">
        <Plus className="w-5 h-5" />
      </button>
    )}
  </th>
)

const DimensionalTable = ({
  pin, box, inspectionKind, showPin, showBox, pinNo2Enabled, boxNo2Enabled, onChange, onAddNo2, onRemoveNo2,
}: {
  pin: CTHPinDimensional
  box: CTHBoxDimensional
  inspectionKind: CTHFormData['inspectionKind']
  showPin: boolean
  showBox: boolean
  pinNo2Enabled: boolean
  boxNo2Enabled: boolean
  onChange: (side: 'pin' | 'box', field: string, slot: keyof CTHMeasurementPair, value: string) => void
  onAddNo2: (side: 'pin' | 'box') => void
  onRemoveNo2: (side: 'pin' | 'box') => void
}) => (
  <div className="overflow-x-auto border border-slate-200 rounded-md bg-white">
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-slate-50">
          {showPin && (
            <>
              <th className={`px-2 py-3 text-left ${sideHeaderCls}`}>PIN</th>
              <th className={`px-2 py-3 text-center ${sideHeaderCls}`}>No 1</th>
              <No2Header enabled={pinNo2Enabled} side="pin" onAdd={onAddNo2} onRemove={onRemoveNo2} />
            </>
          )}
          {showBox && (
            <>
              <th className={`px-2 py-3 text-left ${sideHeaderCls}`}>BOX</th>
              <th className={`px-2 py-3 text-center ${sideHeaderCls}`}>No 1</th>
              <No2Header enabled={boxNo2Enabled} side="box" onAdd={onAddNo2} onRemove={onRemoveNo2} />
            </>
          )}
        </tr>
      </thead>
      <tbody>
        {dimensionalRowsForKind(inspectionKind).map((row) => (
          <tr key={`${row.pinField}-${row.boxField}`}>
            {showPin && (
              <>
                <td className="px-2 py-2 bg-slate-50 font-medium whitespace-nowrap">{row.pinLabel}</td>
                <td className="px-1 py-1"><input value={pin[row.pinField].no1} onChange={(e) => onChange('pin', row.pinField, 'no1', e.target.value)} className={cellInputCls} /></td>
                <td className="px-1 py-1">{pinNo2Enabled ? <input value={pin[row.pinField].no2} onChange={(e) => onChange('pin', row.pinField, 'no2', e.target.value)} className={cellInputCls} /> : <div className="h-12" />}</td>
              </>
            )}
            {showBox && (
              <>
                <td className="px-2 py-2 bg-slate-50 font-medium whitespace-nowrap">{row.boxLabel}</td>
                <td className="px-1 py-1"><input value={box[row.boxField].no1} onChange={(e) => onChange('box', row.boxField, 'no1', e.target.value)} className={cellInputCls} /></td>
                <td className="px-1 py-1">{boxNo2Enabled ? <input value={box[row.boxField].no2} onChange={(e) => onChange('box', row.boxField, 'no2', e.target.value)} className={cellInputCls} /> : <div className="h-12" />}</td>
              </>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)

const VisualTable = ({
  visualPin, visualBox, visualBody, inspectionKind, showPin, showBox, pinNo2Enabled, boxNo2Enabled, onToggle, onToggleBody, onAddNo2, onRemoveNo2,
}: {
  visualPin: CTHPinVisual
  visualBox: CTHBoxVisual
  visualBody: CTHBodyVisual
  inspectionKind: CTHFormData['inspectionKind']
  showPin: boolean
  showBox: boolean
  pinNo2Enabled: boolean
  boxNo2Enabled: boolean
  onToggle: (side: 'visualPin' | 'visualBox', criterion: string, slot: keyof CTHMeasurementPair, current: CTHVisualResult) => void
  onToggleBody: (key: keyof CTHBodyVisual) => void
  onAddNo2: (side: 'pin' | 'box') => void
  onRemoveNo2: (side: 'pin' | 'box') => void
}) => {
  const showBody = inspectionKind === 'RSC'
  const rows = visualRowsForKind(inspectionKind)
  const bodyFields = rows.flatMap((row) =>
    row.bodyKey && row.bodyLabel ? [{ key: row.bodyKey, label: row.bodyLabel }] : [],
  )
  return (
    <div className="overflow-x-auto border border-slate-200 rounded-md bg-white">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50">
            {showPin && (
              <>
                <th className={`px-2 py-3 text-left ${sideHeaderCls}`}>PIN</th>
                <th className={`px-2 py-3 text-center ${sideHeaderCls}`}>No 1</th>
                <No2Header enabled={pinNo2Enabled} side="pin" onAdd={onAddNo2} onRemove={onRemoveNo2} />
              </>
            )}
            {showBody && (
              <>
                <th className={`px-2 py-3 text-left ${sideHeaderCls}`}>BODY</th>
                <th className={`px-2 py-3 text-center ${sideHeaderCls}`} />
              </>
            )}
            {showBox && (
              <>
                <th className={`px-2 py-3 text-left ${sideHeaderCls}`}>BOX</th>
                <th className={`px-2 py-3 text-center ${sideHeaderCls}`}>No 1</th>
                <No2Header enabled={boxNo2Enabled} side="box" onAdd={onAddNo2} onRemove={onRemoveNo2} />
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const bodyField = bodyFields[index]
            return (
            <tr key={`${row.pinKey}-${row.boxKey}-${row.bodyKey ?? ''}`}>
              {showPin && (
                <>
                  <td className="px-2 py-2 bg-slate-50 font-medium whitespace-nowrap">{row.pinLabel}</td>
                  <td className="px-1 py-1"><VisualButton value={visualPin[row.pinKey].no1} onClick={() => onToggle('visualPin', row.pinKey, 'no1', visualPin[row.pinKey].no1)} /></td>
                  <td className="px-1 py-1">{pinNo2Enabled ? <VisualButton value={visualPin[row.pinKey].no2} onClick={() => onToggle('visualPin', row.pinKey, 'no2', visualPin[row.pinKey].no2)} /> : <div className="h-12" />}</td>
                </>
              )}
              {showBody && (
                bodyField ? (
                  <>
                    <td className="px-2 py-2 bg-slate-50 font-medium whitespace-nowrap">{bodyField.label}</td>
                    <td className="px-1 py-1"><VisualButton value={visualBody[bodyField.key]} onClick={() => onToggleBody(bodyField.key)} /></td>
                  </>
                ) : (
                  <>
                    <td className="px-2 py-2" />
                    <td className="px-1 py-1" />
                  </>
                )
              )}
              {showBox && (
                <>
                  <td className="px-2 py-2 bg-slate-50 font-medium whitespace-nowrap">{row.boxLabel}</td>
                  <td className="px-1 py-1">
                    {row.boxFixed === 'N/A'
                      ? <FixedVisual value="N/A" />
                      : <VisualButton value={visualBox[row.boxKey].no1} onClick={() => onToggle('visualBox', row.boxKey, 'no1', visualBox[row.boxKey].no1)} />}
                  </td>
                  <td className="px-1 py-1">
                    {boxNo2Enabled
                      ? (row.boxFixed === 'N/A'
                        ? <FixedVisual value="N/A" />
                        : <VisualButton value={visualBox[row.boxKey].no2} onClick={() => onToggle('visualBox', row.boxKey, 'no2', visualBox[row.boxKey].no2)} />)
                      : <div className="h-12" />}
                  </td>
                </>
              )}
            </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

const FixedVisual = ({ value }: { value: string }) => (
  <div className="w-full min-h-[44px] h-11 text-sm font-semibold rounded-md border border-slate-200 bg-slate-50 text-slate-400 inline-flex items-center justify-center">
    {value}
  </div>
)

const SummaryStatus = ({ ok, rejected }: { ok: boolean; rejected: boolean }) => (
  <div className={`px-3 py-3 text-center font-semibold ${ok ? 'text-emerald-700' : rejected ? 'text-red-700' : 'text-slate-400'}`}>
    {ok ? 'OK' : rejected ? 'REJ' : '—'}
  </div>
)

const VisualButton = ({ value, onClick }: { value: CTHVisualResult; onClick: () => void }) => {
  const tone = value === 'REJ'
    ? 'bg-red-50 text-red-700 border-red-300'
    : value === 'OK'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
      : 'bg-slate-100 text-slate-400 border-slate-300'
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full min-h-[44px] h-11 text-sm font-semibold rounded-md border ${tone}`}
    >
      {value === 'REJ' ? 'REJ' : 'OK'}
    </button>
  )
}

export default CTHTestForm
